import { forwardRef, useMemo } from "react";

import { Box, BoxProps, Flex, InputProps } from "@chakra-ui/react";

import { TranslatedProductSearchUnit } from "shared/models/productSearchUnit/types";
import { CheckButton } from "ui/CheckButton/CheckButton";
import { Form } from "ui/Form/Form";
import { InboundFruitOrVeggyChips } from "ui/InboundFruitOrVeggyChips/InboundFruitOrVeggyChips";
import { Number } from "ui/Number/Number";
import { ProductFacing } from "ui/ProductFacing/ProductFacing";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { ProductQuantityInput } from "ui/ProductQuantityInput/ProductQuantityInput";
import { Shelf } from "ui/Shelf/Shelf";
import { Status } from "ui/Status/Status";
import { TitleXS } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

export type InboundDroppingListItemCardTranslatedLabelsProps = {
  singleUnitsLabel: string;
  handlingUnitsLabel: string;
  totalUnitsLabel: string;
  inboundedLabel: string;
  bioLabel: string;
  nonBioLabel: string;
  outboundedLabel: string;
  outboundLabel: string;
  droppingLabel: string;
  inStockLabel: string;
};

export type QuantityFieldProps = {
  inputProps: InputProps;
  onSubmitForm: React.FormEventHandler;
};

export type InboundUnitListItemDataProps = Pick<
  TranslatedProductSearchUnit,
  | "shelfLetter"
  | "shelfNumber"
  | "productName"
  | "productSku"
  | "productImageUrl"
  | "numberOfShelfFacings"
  | "numberOfShelfStackings"
  | "isShelvedInHandlingUnits"
  | "unitType"
  | "countryOfOriginName"
  | "countryOfOriginCode"
  | "isProductBio"
>;

export type InboundDroppingListItemCardProps = {
  stockUpdatePlanTotal?: number;
  onClickCard: () => void;
  onConfirmQuantity: () => void;
  isQuantityCheckRequired: boolean;
  isQuantityConfirmed: boolean;
  displayQuantity: number;
  isStockUpdateComplete: boolean;
  displayAsHandlingUnit: boolean;
  isPartiallyOutbounded?: boolean;
  isOutbounded?: boolean;
  stockOnShelf: number;
  showSKU?: boolean;
  isInEditMode?: boolean;
  quantityFieldProps?: QuantityFieldProps;
  displayProductFacing?: boolean;
} & InboundDroppingListItemCardTranslatedLabelsProps &
  InboundUnitListItemDataProps &
  BoxProps;

export const InboundDroppingListItemCard = forwardRef<
  HTMLDivElement,
  InboundDroppingListItemCardProps
>(
  (
    {
      shelfLetter,
      shelfNumber,
      productName,
      productSku,
      productImageUrl,
      isShelvedInHandlingUnits,
      numberOfShelfFacings,
      numberOfShelfStackings,
      unitType,
      displayQuantity,
      inboundedLabel,
      singleUnitsLabel,
      handlingUnitsLabel,
      stockUpdatePlanTotal,
      totalUnitsLabel,
      bioLabel,
      nonBioLabel,
      outboundedLabel,
      outboundLabel,
      countryOfOriginName,
      countryOfOriginCode,
      isProductBio,
      isStockUpdateComplete,
      displayAsHandlingUnit,
      onClickCard,
      onConfirmQuantity,
      isQuantityCheckRequired = false,
      isQuantityConfirmed,
      isPartiallyOutbounded = false,
      isOutbounded = false,
      droppingLabel,
      inStockLabel,
      stockOnShelf,
      isInEditMode = false,
      quantityFieldProps,
      displayProductFacing = true,
      ...rest
    },
    ref,
  ) => {
    const isFruitOrVeggy = isNotNullNorUndefined(countryOfOriginName);
    const bgColorForQuantityIncomplete = displayAsHandlingUnit ? "orange.100" : "marine.100";

    const bgColorForQuantity = isStockUpdateComplete ? "veggie.100" : bgColorForQuantityIncomplete;

    const colorForQuantity = isStockUpdateComplete ? "green.500" : "grey.800";

    const displayedShelf =
      isNotNullNorUndefined(shelfLetter) && isNotNullNorUndefined(shelfNumber) ? (
        <Shelf gridColumnStart="3" letter={shelfLetter} number={shelfNumber} />
      ) : (
        <Status bg="grey.200" textColor="black" gridColumnStart="3" label="N/A" />
      );

    const displayedStatus = useMemo(() => {
      if (isOutbounded) {
        return (
          <Status
            bg="purple.500"
            textColor="white"
            label={isStockUpdateComplete ? outboundedLabel : outboundLabel}
          />
        );
      }
      if (isStockUpdateComplete) {
        return <Status bg="veggie.500" textColor="white" label={inboundedLabel} />;
      }
      if (isFruitOrVeggy) {
        return (
          <InboundFruitOrVeggyChips
            bioLabel={bioLabel}
            nonBioLabel={nonBioLabel}
            countryOfOriginName={countryOfOriginName}
            isBio={!!isProductBio}
          />
        );
      }
      if (displayProductFacing) {
        return (
          <ProductFacing
            isShelvedInHandlingUnits={isShelvedInHandlingUnits}
            numberOfFacings={numberOfShelfFacings}
            numberOfStacking={numberOfShelfStackings}
          />
        );
      }
      return null;
    }, [
      isStockUpdateComplete,
      isOutbounded,
      inboundedLabel,
      outboundedLabel,
      outboundLabel,
      isFruitOrVeggy,
      bioLabel,
      nonBioLabel,
      isProductBio,
      countryOfOriginName,
      isShelvedInHandlingUnits,
      numberOfShelfFacings,
      numberOfShelfStackings,
      displayProductFacing,
    ]);

    const updatedStock = isStockUpdateComplete
      ? stockOnShelf + (stockUpdatePlanTotal ?? 0)
      : stockOnShelf;

    const renderQuantityDisplay = useMemo(() => {
      if (isInEditMode && quantityFieldProps) {
        return (
          <Form onSubmit={quantityFieldProps.onSubmitForm}>
            <ProductQuantityInput
              displayAsHandlingUnit={false}
              {...quantityFieldProps.inputProps}
              hasWarning={false}
              data-testid="unit-quantity-input"
              autoFocus
            />
          </Form>
        );
      }
      return (
        <Number
          content={displayQuantity.toString()}
          color={colorForQuantity}
          bgColor={bgColorForQuantity}
          data-testid="unit-quantity"
        />
      );
    }, [bgColorForQuantity, colorForQuantity, displayQuantity, isInEditMode, quantityFieldProps]);

    const getLabel = () => {
      if (isInEditMode) {
        return droppingLabel;
      }
      return displayAsHandlingUnit ? handlingUnitsLabel : singleUnitsLabel;
    };

    const showLeftSideBar = isPartiallyOutbounded || isStockUpdateComplete;

    const css = showLeftSideBar
      ? {
          borderLeft: "8px",
          borderLeftColor: isPartiallyOutbounded ? "purple.500" : "veggie.500",
          borderLeftRadius: "8px",
        }
      : {};

    return (
      <Box
        onClick={(e) => {
          e.stopPropagation();
          if (!isInEditMode) {
            onClickCard();
          }
        }}
        bg="white"
        ref={ref}
        data-testid="inbound-dropping-card"
        {...css}
        {...rest}
      >
        <Flex alignItems="center" justifyContent="space-between" p="s200">
          {displayedStatus}
          {displayedShelf}
        </Flex>
        <ProductInformation
          bg="grey.100"
          name={productName ?? ""}
          sku={productSku}
          imageUrl={productImageUrl ?? ""}
          showSKU={!productName}
          stock={updatedStock}
          showProductFacing={displayProductFacing}
        />
        <Flex justifyContent="space-between" p="s200">
          <Flex direction="column">
            <TitleXS mb="s100" color="grey.800">
              {getLabel()}
            </TitleXS>
            <Flex justifyContent="flex-start" alignItems="center" gap="s150">
              {renderQuantityDisplay}
              {isQuantityCheckRequired && (
                <CheckButton
                  checked={isQuantityConfirmed}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isQuantityConfirmed) {
                      onConfirmQuantity();
                    }
                  }}
                />
              )}
            </Flex>
          </Flex>
          <Flex gap="s200">
            <Flex direction="column" alignItems="center">
              <TitleXS mb="s100" color="grey.800">
                {droppingLabel}
              </TitleXS>
              <Number
                content={isStockUpdateComplete ? "0" : stockUpdatePlanTotal!.toString()}
                color="black"
                bgColor="white"
                data-testid="product-dropping-amount"
              />
            </Flex>
          </Flex>
        </Flex>
      </Box>
    );
  },
);
